// Only edit Segment/Info. Re-serializing a MediaRecorder WebM's unknown-size
// Cluster can accidentally wrap subsequent clusters and break remote seeking.
function readElement(bytes, offset) {
  const read = (position, keepMarker) => {
    const first = bytes[position];
    if (!first) throw Error('Invalid WebM element');
    let length = 1, marker = 128;
    while (!(first & marker) && length < 8) {length++; marker /= 2;}
    if (position + length > bytes.length) throw Error('Incomplete WebM header');
    let value = keepMarker ? first : first & (marker - 1);
    let unknown = !keepMarker && value === marker - 1;
    for (let i = 1; i < length; i++) {
      value = value * 256 + bytes[position + i];
      unknown = unknown && bytes[position + i] === 255;
    }
    return {length, value, unknown};
  };
  const id = read(offset, true);
  const size = read(offset + id.length, false);
  const start = offset + id.length + size.length;
  return {id: id.value, offset, sizeOffset: offset + id.length,
    sizeLength: size.length, start, end: size.unknown ? Infinity : start + size.value,
    unknown: size.unknown};
}

function writeSize(value, length) {
  if (!Number.isSafeInteger(value) || value >= 2 ** (7 * length) - 1) throw Error('WebM header size overflow');
  const bytes = new Uint8Array(length);
  for (let i = length - 1; i >= 0; i--) {bytes[i] = value % 256; value = Math.floor(value / 256);}
  bytes[0] |= 2 ** (8 - length);
  return bytes;
}

export default async function fixRecordedWebmDuration(blob, durationMs) {
  if (!blob || !Number.isFinite(durationMs) || durationMs <= 0) return blob;
  // Metadata precedes the first Cluster; never load the full recording into RAM.
  const bytes = new Uint8Array(await blob.slice(0, 1024 * 1024).arrayBuffer());
  try {
    let segment;
    for (let offset = 0; offset < bytes.length;) {
      const element = readElement(bytes, offset);
      if (element.id === 0x18538067) {segment = element; break;}
      offset = element.end;
    }
    if (!segment) return blob; // Includes MP4 recordings.
    let info, indexed = false;
    for (let offset = segment.start; offset < bytes.length;) {
      const element = readElement(bytes, offset);
      if (element.id === 0x1f43b675) break;
      if (element.id === 0x114d9b74 && element.end > element.start) indexed = true;
      if (element.id === 0x1549a966) info = element;
      if (!Number.isFinite(element.end) || element.end <= offset) return blob;
      offset = element.end;
    }
    if (!info || info.end > bytes.length) return blob;
    let duration, scale = 1000000;
    for (let offset = info.start; offset < info.end;) {
      const element = readElement(bytes, offset);
      if (element.end > info.end || element.end <= offset) return blob;
      if (element.id === 0x4489) duration = element;
      if (element.id === 0x2ad7b1) {
        scale = 0;
        for (let i = element.start; i < element.end; i++) scale = scale * 256 + bytes[i];
      }
      offset = element.end;
    }
    if (!scale) return blob;
    const value = durationMs * 1000000 / scale;
    if (duration) {
      const length = duration.end - duration.start;
      if (length !== 4 && length !== 8) return blob;
      const view = new DataView(bytes.buffer);
      const existing = length === 4 ? view.getFloat32(duration.start) : view.getFloat64(duration.start);
      if (Number.isFinite(existing) && existing > 0) return blob;
      const replacement = new Uint8Array(length);
      const output = new DataView(replacement.buffer);
      if (length === 4) output.setFloat32(0, value); else output.setFloat64(0, value);
      return new Blob([blob.slice(0, duration.start), replacement, blob.slice(duration.end)], {type: blob.type});
    }
    // Existing seek offsets must not be shifted. MediaRecorder's SeekHead is empty.
    if (indexed) return blob;
    const addition = new Uint8Array(11);
    addition.set([0x44, 0x89, 0x88]);
    new DataView(addition.buffer).setFloat64(3, value);
    const header = bytes.slice(0, info.start);
    header.set(writeSize(info.end - info.start + addition.length, info.sizeLength), info.sizeOffset);
    if (!segment.unknown) header.set(writeSize(segment.end - segment.start + addition.length, segment.sizeLength), segment.sizeOffset);
    return new Blob([header, blob.slice(info.start, info.end), addition, blob.slice(info.end)], {type: blob.type});
  } catch {
    // Preserve the original recording rather than risk damaging an unknown format.
    return blob;
  }
}
